import React, { useCallback } from "react";
import { mergeClassNames } from "../../design-tokens";
import { Button } from "../Button/Button";
import Avatar from "../Avatar/Avatar";
import styles from "./CommentSection.module.css";

// ── Types ──

export interface CommentItem {
  id: string;
  userName: string;
  comment: string;
  createdAt: string;
}

export interface CommentSectionLabels {
  title?: string;
  placeholder?: string;
  submit?: string;
  empty?: string;
}

export interface CommentSectionProps {
  /** Lista de comentários existentes */
  comments: CommentItem[];
  /** Valor atual do campo de texto */
  value: string;
  /** Callback quando o texto muda */
  onChange: (value: string) => void;
  /** Callback ao enviar o comentário */
  onSubmit: () => void;
  /** Desabilita input e envio */
  disabled?: boolean;
  /** Altura máxima da lista de comentários (overflow scroll) */
  maxHeight?: number | string;
  /** Labels customizáveis */
  labels?: CommentSectionLabels;
  /** Classes CSS adicionais */
  className?: string;
  /** Esconde o ícone do cabeçalho */
  hideIcon?: boolean;
}

// ── Helpers ──

const DEFAULT_LABELS: Required<CommentSectionLabels> = {
  title: "Comentários",
  placeholder: "Escreva um comentário...",
  submit: "Enviar",
  empty: "Nenhum comentário ainda.",
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ── Component ──

export const CommentSection: React.FC<CommentSectionProps> = ({
  comments,
  value,
  onChange,
  onSubmit,
  disabled = false,
  maxHeight = 480,
  labels: userLabels,
  className,
  hideIcon = false,
}) => {
  const labels = { ...DEFAULT_LABELS, ...userLabels };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (value.trim() && !disabled) onSubmit();
      }
    },
    [disabled, onSubmit, value],
  );

  const canSubmit = value.trim().length > 0 && !disabled;

  return (
    <div className={mergeClassNames(styles.root, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>
          {!hideIcon && (
            <span className="material-symbols-outlined" aria-hidden="true">chat</span>
          )}
          {labels.title}
        </h3>
      </div>

      {/* Comment list */}
      {comments.length > 0 ? (
        <div
          className={styles.commentList}
          style={{ maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight }}
        >
          {comments.map((item) => (
            <div key={item.id} className={styles.commentItem}>
              <div className={styles.commentAvatar}>
                <Avatar type="person" name={item.userName} />
              </div>
              <div className={styles.commentBubble}>
                <div className={styles.commentHeader}>
                  <span className={styles.commentAuthor}>{item.userName}</span>
                  <span className={styles.commentDate}>{formatDate(item.createdAt)}</span>
                </div>
                <span className={styles.commentText}>{item.comment}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <span className={mergeClassNames("material-symbols-outlined", styles.emptyIcon)} aria-hidden="true">
            forum
          </span>
          {labels.empty}
        </div>
      )}

      {/* Composer (Input area) */}
      <div className={mergeClassNames(styles.composer, disabled && styles.composerDisabled)}>
        <textarea
          className={styles.textarea}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={labels.placeholder}
          disabled={disabled}
          rows={3}
        />
        <div className={styles.composerFooter}>
          {!disabled && (
            <span className={styles.composerHint}>
              Pressione <strong>Ctrl + Enter</strong> para enviar
            </span>
          )}
          {labels.submit && (
            <Button
              label={labels.submit}
              variant="outlined"
              severity="primary"
              onClick={onSubmit}
              disabled={!canSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentSection;
